import { listSupportedLocales, toBcp47 } from '../../domain/i18n/locales';

/**
 * Keep the document head honest about which locale is on screen.
 *
 * Three things have to move together on every locale change, and all three are
 * SEO-load-bearing (FR-01, NFR-04):
 *   - `<html lang>` so assistive tech and the browser pick the right voice and
 *     hyphenation,
 *   - `<link rel="canonical">` so the market-language pages do not compete with
 *     each other,
 *   - one `<link rel="alternate" hreflang>` per shipped locale plus `x-default`,
 *     so a search engine can offer the right one.
 *
 * Managed imperatively rather than in JSX because these tags live in <head>,
 * outside the React root.
 */

const MANAGED_ATTRIBUTE = 'data-plant-locale-link';

/**
 * The one public origin. Firebase serves the same content on the custom domain
 * AND on two firebase-owned mirrors, so without this every mirror would
 * self-canonicalise and compete with the real domain for the same content — the
 * exact duplicate-content split the hreflang graph exists to prevent.
 *
 * Anything not in this set (localhost, a preview channel) canonicalises to
 * itself, so a preview never claims to be production.
 */
export const PRODUCTION_ORIGIN = 'https://plant.habb-me.com';

const PRODUCTION_HOSTNAMES = new Set([
    'plant.habb-me.com',
    'plant-habb-me.web.app',
    'plant-habb-me.firebaseapp.com',
]);

export function resolveCanonicalOrigin(locationObject) {
    if (!locationObject) {
        return PRODUCTION_ORIGIN;
    }

    return PRODUCTION_HOSTNAMES.has(locationObject.hostname)
        ? PRODUCTION_ORIGIN
        : locationObject.origin;
}

function upsertLink(documentObject, rel, href, hreflang) {
    const selector = hreflang
        ? `link[${MANAGED_ATTRIBUTE}][rel="${rel}"][hreflang="${hreflang}"]`
        : `link[${MANAGED_ATTRIBUTE}][rel="${rel}"]`;
    const existingLink = documentObject.head.querySelector(selector);
    const link = existingLink || documentObject.createElement('link');

    link.setAttribute(MANAGED_ATTRIBUTE, '');
    link.setAttribute('rel', rel);
    link.setAttribute('href', href);

    if (hreflang) {
        link.setAttribute('hreflang', hreflang);
    }

    if (!existingLink) {
        documentObject.head.appendChild(link);
    }
}

export function buildAlternateLinks(routePath, origin) {
    const normalizedRoute = routePath === '/' ? '' : routePath.replace(/\/$/u, '');

    return listSupportedLocales().map((localeCode) => ({
        hreflang: toBcp47(localeCode),
        href: `${origin}/${localeCode}${normalizedRoute}`,
    }));
}

export function applyLocaleDocumentMetadata({
    localeCode,
    routePath = '/',
    documentObject = typeof document === 'undefined' ? null : document,
    origin = resolveCanonicalOrigin(typeof window === 'undefined' ? null : window.location),
}) {
    if (!documentObject || !localeCode) {
        return;
    }

    const bcp47 = toBcp47(localeCode);

    if (!bcp47) {
        return;
    }

    documentObject.documentElement.setAttribute('lang', bcp47);

    const normalizedRoute = routePath === '/' ? '' : routePath.replace(/\/$/u, '');
    upsertLink(documentObject, 'canonical', `${origin}/${localeCode}${normalizedRoute}`);

    buildAlternateLinks(routePath, origin).forEach(({ hreflang, href }) => {
        upsertLink(documentObject, 'alternate', href, hreflang);
    });

    // x-default points at the unprefixed root, which negotiates per visitor —
    // the correct target for "we have no page specifically for your locale".
    upsertLink(documentObject, 'alternate', `${origin}/`, 'x-default');
}
